import React, { useState, useEffect } from "react";
import * as R from "ramda";
import { Collapse, Icon } from "antd";
import { compose } from "recompose";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { dispatchOnMount, spinWhile } from "@worcket/common/hocs";
import { connect, useSelector } from "react-redux";

import {
  fetchCompanyPurchasesLite,
  FETCH_COMPANY_PURCHASES_LITE
} from "../../../store/actions/purchases";
import { FETCH_TESTS } from "../../../store/actions/catalogs";
import { isValidJSON } from "../../../utils/json";
import Spinner from "../../feedback/Spinner";

import TestDetail from "./TestDetail";

import styled from "styled-components/macro";

const TestsContainer = styled.div`
  margin: 32px 20px 0 30px;

  .ant-collapse {
    margin-bottom: 1em;
  }
  h3 {
    margin-bottom: 1em;
  }
  .title {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 1em;
    i {
      font-size: 1.5em;
      margin-left: 8px;
    }
    h3 {
      margin: 0;
    }
    .info-circle {
      margin-left: 5px;
      font-size: 1em;
    }
  }
`;

function Tests({ tests, match, history }) {
  const { t } = useTranslation(["opportunity"]);

  const defaultTest = R.propOr(
    null,
    "type",
    tests.find(({ type }) =>
      history.location.pathname.includes(
        t(`opportunity:process.candidateDetails.${type}.slug`)
      )
    )
  );

  const [activeTest, setActiveTest] = useState(defaultTest);

  const changeTest = tabs => {
    const test = R.last(tabs);
    const urlSubfix = test
      ? `/${t(`opportunity:process.candidateDetails.${test}.slug`)}`
      : "";
    history.push(match.url + urlSubfix);
    setActiveTest(test);
  };

  useEffect(() => {
    const test = R.propOr(
      null,
      "type",
      tests.find(({ type }) =>
        history.location.pathname.endsWith(
          t(`opportunity:process.candidateDetails.${type}.slug`)
        )
      )
    );
    if (test && test !== activeTest) {
      setActiveTest(test);
    }
  }, [history.location.pathname, activeTest, t, tests]);

  const hasSuccessProbabilityTest = useSelector(state => {
    let res = R.pathOr(
      undefined,
      ["company", "services", "successProbabilityTest"],
      state
    );
    if (res === undefined) {
      res = R.pathOr(
        false,
        ["headquarter", "services", "successProbabilityTest"],
        state
      );
    }
    return res;
  });

  const filterTests = () => {
    if (hasSuccessProbabilityTest) return tests;
    return R.reject(
      R.both(R.propEq("source", "worcket"), R.propEq("testIdentifier", "3")),
      tests
    );
  };
  const filteredTests = filterTests();
  const [premiumTests, freeTests] = R.partition(
    R.propEq("paid", true),
    filteredTests
  );

  return (
    <TestsContainer>
      {freeTests.length > 0 && (
        <div data-test="free-tests" style={{ marginBottom: 32 }}>
          {/* <h3>{t("opportunity:process.candidateDetails.freeTests")}</h3> */}
          <Collapse activeKey={activeTest} onChange={changeTest}>
            {freeTests.map(test => (
              <TestDetail key={test.type} test={test} />
            ))}
          </Collapse>
        </div>
      )}
      {premiumTests.length > 0 && (
        <div data-test="premium-tests">
          <div className="title">
            <h3>{t("opportunity:process.candidateDetails.premiumTests")}</h3>
            <Icon type="star" theme="twoTone" twoToneColor="#3D64FF" />
          </div>

          <Collapse activeKey={activeTest} onChange={changeTest}>
            {premiumTests.map(test => (
              <TestDetail key={test.type} test={test} />
            ))}
          </Collapse>
        </div>
      )}
    </TestsContainer>
  );
}

const getTestInfo = (test, testsCatalog = []) => {
  const testDefinition =
    testsCatalog.find(
      ({ source, idTest }) =>
        source === (test.source || "gh") &&
        Number(idTest) === Number(test.testIdentifier)
    ) || {};

  return { type: testDefinition.key, source: testDefinition.source };
};

const enhancer = compose(
  withRouter,
  dispatchOnMount(fetchCompanyPurchasesLite),
  spinWhile(FETCH_COMPANY_PURCHASES_LITE, { initial: true }, <Spinner />),
  connect(state => {
    const candidate = R.path(["candidates", "selected"], state);
    const testsCatalog = FETCH_TESTS.getResponse(state) || [];

    return {
      tests: R.filter(Boolean, [
        // If english test is not successfull don't show anything
        R.pathEq(["englishTest", "status"], "Success", candidate)
          ? {
              ...candidate.englishTest,
              type: "english"
            }
          : null,
        ...R.pipe(
          R.pathOr([], ["humanTests"]),
          R.filter(
            ({ serviceCallResult, serviceCallRequest }) =>
              serviceCallResult === "success" &&
              serviceCallRequest &&
              isValidJSON(serviceCallRequest)
          ),
          R.map(test => ({
            ...test,
            ...getTestInfo(test, testsCatalog)
          }))
        )(candidate),
        ...R.pipe(
          R.pathOr([], ["tests"]),
          R.map(test => ({
            ...test,
            ...getTestInfo(test, testsCatalog)
          }))
        )(candidate)
      ])
    };
  })
);

export default enhancer(Tests);
